import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from 'swr';
import TabPageLayout, { PageLoader, DataCard, EmptyState } from "./TabPageLayout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { 
  Users, UserPlus, Trash2, ExternalLink, CheckCircle2, 
  Clock, Loader2, RefreshCw, Percent, DollarSign, Link2,
  Megaphone, TrendingUp, Copy, Check, Tag, Plus,
  AlertCircle, Sparkles, ArrowRight, ShieldCheck, History,
  XCircle, ArrowUpRight, ArrowDownRight, Unlink, Building2, Package, Handshake
} from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";

const MAX_CODES = 20;

const PartnersAndPromosTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeView, setActiveView] = useState("partners"); // partners, promos
  
  // Partners state
  const [partners, setPartners] = useState([]);
  const [services, setServices] = useState([]);
  const [summary, setSummary] = useState({});
  const [partnerTab, setPartnerTab] = useState("all");
  
  // Shared/Linked Services state
  const [sharedServices, setSharedServices] = useState([]);
  const [loadingShared, setLoadingShared] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [shareToken, setShareToken] = useState("");
  const [linking, setLinking] = useState(false);
  
  // Partner Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showStripeProgressModal, setShowStripeProgressModal] = useState(false);
  const [showEarningsDialog, setShowEarningsDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [earningsHistory, setEarningsHistory] = useState([]);
  const [earningsSummary, setEarningsSummary] = useState({});
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Partner form state
  const [newPartner, setNewPartner] = useState({
    name: "",
    email: "",
    phone: "",
    partnerType: "service_partner",
    defaultSplitPercentage: 10,
    promoCode: "",
    notes: ""
  });
  const [selectedServices, setSelectedServices] = useState([]);

  // Promo codes state
  const [codes, setCodes] = useState([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [promoFormData, setPromoFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    maxUses: '',
    expiresInDays: '',
    isAffiliate: false,
    affiliateName: '',
    affiliateCommission: ''
  });

  // SWR for partners
  const { data: partnersData, error: partnersError, isLoading: partnersLoading, mutate: mutatePartners } = useSWR(
    '/partners',
    async (url) => {
      const [partnersRes, servicesRes, summaryRes] = await Promise.all([
        api.get(url),
        api.get('/services'),
        api.get('/partners/summary')
      ]);
      return {
        partners: partnersRes.data || [],
        services: servicesRes.data?.services || servicesRes.data || [],
        summary: summaryRes.data || {}
      };
    },
    { revalidateOnFocus: true }
  );

  // SWR for promo codes
  const { data: promoData, error: promoError, isLoading: promoLoading, mutate: mutatePromos } = useSWR(
    '/promo-codes',
    async (url) => {
      const res = await api.get(url);
      return res.data || [];
    },
    { revalidateOnFocus: true }
  );

  // Update local state from SWR
  useEffect(() => {
    if (partnersData) {
      setPartners(partnersData.partners);
      setServices(partnersData.services);
      setSummary(partnersData.summary);
    }
  }, [partnersData]);

  useEffect(() => {
    if (promoData) {
      setCodes(promoData);
    }
  }, [promoData]);

  // Check for Stripe Connect return
  useEffect(() => {
    const connectSuccess = searchParams.get('connect_success');
    const refresh = searchParams.get('refresh');
    
    if (connectSuccess === 'true' || refresh === 'true') {
      setShowStripeProgressModal(true);
      setSearchParams({});
      mutatePartners();
    }
  }, [searchParams, setSearchParams, mutatePartners]);

  // Fetch shared services
  const fetchSharedServices = async () => {
    setLoadingShared(true);
    try {
      const response = await api.get('/shared-services/linked');
      setSharedServices(response.data || []);
    } catch (error) {
      console.error('Error fetching shared services:', error);
    } finally {
      setLoadingShared(false);
    }
  };

  useEffect(() => {
    fetchSharedServices();
  }, []);

  // Partner functions
  const handleAddPartner = async () => {
    if (!newPartner.name || !newPartner.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/partners', newPartner);
      toast.success("Partner added successfully");
      setShowAddDialog(false);
      setNewPartner({
        name: "", email: "", phone: "", partnerType: "service_partner",
        defaultSplitPercentage: 10, promoCode: "", notes: ""
      });
      mutatePartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add partner");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!confirm("Delete this partner?")) return;
    try {
      await api.delete(`/partners/${partnerId}`);
      toast.success("Partner deleted");
      mutatePartners();
    } catch (error) {
      toast.error("Failed to delete partner");
    }
  };

  const handleAssignServices = async () => {
    if (!selectedPartner || selectedServices.length === 0) return;
    setSaving(true);
    try {
      await api.post(`/partners/${selectedPartner.id}/services`, {
        serviceIds: selectedServices
      });
      toast.success("Services assigned successfully");
      setShowAssignDialog(false);
      setSelectedServices([]);
      mutatePartners();
    } catch (error) {
      toast.error("Failed to assign services");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, codeId) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(codeId);
    toast.success("Copied!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleLinkService = async () => {
    if (!shareToken.trim()) {
      toast.error("Please enter a share token");
      return;
    }
    setLinking(true);
    try {
      await api.post('/shared-services/link', { shareToken: shareToken.trim() });
      toast.success("Service linked successfully!");
      setShowLinkDialog(false);
      setShareToken("");
      fetchSharedServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link service");
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkService = async (linkId) => {
    if (!confirm("Unlink this service?")) return;
    try {
      await api.delete(`/shared-services/linked/${linkId}`);
      toast.success("Service unlinked");
      fetchSharedServices();
    } catch (error) {
      toast.error("Failed to unlink service");
    }
  };

  // Promo code functions
  const resetPromoForm = () => {
    setPromoFormData({
      code: '', discountType: 'percentage', discountValue: '',
      maxUses: '', expiresInDays: '', isAffiliate: false,
      affiliateName: '', affiliateCommission: ''
    });
    setEditingCode(null);
  };

  const handlePromoSubmit = async () => {
    if (!promoFormData.code || !promoFormData.discountValue) {
      toast.error('Code and discount value are required');
      return;
    }
    if (!editingCode && codes.length >= MAX_CODES) {
      toast.error(`Maximum ${MAX_CODES} discount codes allowed`);
      return;
    }

    try {
      let expirationDate = null;
      if (promoFormData.expiresInDays) {
        const expDate = new Date();
        expDate.setDate(expDate.getDate() + parseInt(promoFormData.expiresInDays));
        expirationDate = expDate.toISOString();
      }

      const payload = {
        code: promoFormData.code.toUpperCase(),
        discountType: promoFormData.discountType,
        discountValue: parseFloat(promoFormData.discountValue),
        maxUses: promoFormData.maxUses ? parseInt(promoFormData.maxUses) : null,
        expirationDate,
        isAffiliate: promoFormData.isAffiliate,
        affiliateName: promoFormData.isAffiliate ? promoFormData.affiliateName : null,
        affiliateCommission: promoFormData.isAffiliate ? parseFloat(promoFormData.affiliateCommission) : null
      };

      if (editingCode) {
        await api.put(`/promo-codes/${editingCode.id}`, payload);
        toast.success('Promo code updated');
      } else {
        await api.post('/promo-codes', payload);
        toast.success('Promo code created');
      }
      
      setShowPromoModal(false);
      resetPromoForm();
      mutatePromos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save promo code');
    }
  };

  const handleDeletePromo = async (codeId) => {
    if (!confirm('Delete this promo code?')) return;
    try {
      await api.delete(`/promo-codes/${codeId}`);
      toast.success('Promo code deleted');
      mutatePromos();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const editPromo = (code) => {
    setEditingCode(code);
    setPromoFormData({
      code: code.code,
      discountType: code.discountType,
      discountValue: code.discountValue.toString(),
      maxUses: code.maxUses?.toString() || '',
      expiresInDays: '',
      isAffiliate: code.isAffiliate || false,
      affiliateName: code.affiliateName || '',
      affiliateCommission: code.affiliateCommission?.toString() || ''
    });
    setShowPromoModal(true);
  };

  // Filter partners by tab
  const filteredPartners = partnerTab === "all" ? partners : 
    partners.filter(p => p.partnerType === partnerTab);

  const isLoading = partnersLoading || promoLoading;

  if (isLoading) {
    return (
      <TabPageLayout title="Partners & Promos">
        <PageLoader />
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Affiliates"
      subtitle="Manage partners and promo codes"
      actions={
        <Button
          onClick={() => activeView === 'partners' ? setShowAddDialog(true) : (resetPromoForm(), setShowPromoModal(true))}
          className="bg-blue-600 hover:bg-blue-700 text-white h-10"
        >
          <Plus className="w-4 h-4 mr-2" />
          {activeView === 'partners' ? 'Add Partner' : 'New Code'}
        </Button>
      }
    >
      <div className="space-y-6">
        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {[
              { id: 'partners', label: 'Partners', icon: Handshake },
              { id: 'promos', label: 'Promo Codes', icon: Tag }
            ].map((view) => {
              const Icon = view.icon;
              return (
                <button
                  key={view.id}
                  onClick={() => setActiveView(view.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeView === view.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  } ${view.id !== 'partners' ? 'border-l border-gray-200' : ''}`}
                >
                  <Icon className="w-4 h-4" />
                  {view.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Partners View */}
        {activeView === 'partners' && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <DataCard>
                <div className="text-center py-3">
                  <div className="text-2xl font-bold text-gray-900">{partners.length}</div>
                  <div className="text-sm text-gray-500">Total Partners</div>
                </div>
              </DataCard>
              <DataCard>
                <div className="text-center py-3">
                  <div className="text-2xl font-bold text-green-600">{partners.filter(p => p.stripeAccountId).length}</div>
                  <div className="text-sm text-gray-500">Connected</div>
                </div>
              </DataCard>
              <DataCard>
                <div className="text-center py-3">
                  <div className="text-2xl font-bold text-blue-600">${(summary.totalEarnings || 0).toFixed(2)}</div>
                  <div className="text-sm text-gray-500">Total Earnings</div>
                </div>
              </DataCard>
              <DataCard>
                <div className="text-center py-3">
                  <div className="text-2xl font-bold text-purple-600">{sharedServices.length}</div>
                  <div className="text-sm text-gray-500">Linked Services</div>
                </div>
              </DataCard>
            </div>

            {/* Partner Type Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['all', 'service_partner', 'affiliate', 'referrer'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setPartnerTab(tab)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    partnerTab === tab
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tab === 'all' ? 'All' : tab === 'service_partner' ? 'Service Partners' : 
                   tab === 'affiliate' ? 'Affiliates' : 'Referrers'}
                </button>
              ))}
            </div>

            {/* Partners List */}
            {filteredPartners.length === 0 ? (
              <EmptyState
                icon={<Users />}
                title="No partners yet"
                description="Add your first partner to start revenue sharing"
                action={
                  <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white h-10">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Partner
                  </Button>
                }
              />
            ) : (
              <div className="grid gap-4">
                {filteredPartners.map((partner) => (
                  <DataCard key={partner.id}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{partner.name}</h3>
                          <p className="text-sm text-gray-500">{partner.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {partner.partnerType?.replace('_', ' ')}
                            </Badge>
                            {partner.stripeAccountId ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Connected
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600 border-amber-200 text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700">
                          {partner.defaultSplitPercentage}% split
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePartner(partner.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </DataCard>
                ))}
              </div>
            )}

            {/* Linked Services Section */}
            <DataCard 
              title="Linked Services" 
              subtitle="Services shared from partner organizations"
              actions={
                <Button variant="outline" size="sm" onClick={() => setShowLinkDialog(true)} className="h-9">
                  <Link2 className="w-4 h-4 mr-2" />
                  Link Service
                </Button>
              }
            >
              {sharedServices.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No linked services yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sharedServices.map((service) => (
                    <div key={service.linkId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{service.name}</p>
                        <p className="text-xs text-gray-500">From: {service.sourceOrgName || 'Partner'}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnlinkService(service.linkId)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Unlink className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </DataCard>
          </>
        )}

        {/* Promo Codes View */}
        {activeView === 'promos' && (
          <>
            {/* Usage Stats */}
            <div className="flex items-center justify-between px-4 py-3 bg-blue-50 rounded-lg">
              <span className="text-sm text-blue-700">
                {codes.length} / {MAX_CODES} codes used
              </span>
              <div className="w-32 h-2 bg-blue-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 rounded-full transition-all"
                  style={{ width: `${(codes.length / MAX_CODES) * 100}%` }}
                />
              </div>
            </div>

            {/* Codes List */}
            {codes.length === 0 ? (
              <EmptyState
                icon={<Tag />}
                title="No promo codes"
                description="Create your first discount code"
                action={
                  <Button onClick={() => { resetPromoForm(); setShowPromoModal(true); }} className="bg-blue-600 hover:bg-blue-700 text-white h-10">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Code
                  </Button>
                }
              />
            ) : (
              <div className="grid gap-4">
                {codes.map((code) => (
                  <DataCard key={code.id}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          code.discountType === 'percentage' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          {code.discountType === 'percentage' 
                            ? <Percent className="w-5 h-5 text-green-600" />
                            : <DollarSign className="w-5 h-5 text-blue-600" />
                          }
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-gray-900">{code.code}</span>
                            <button
                              onClick={() => copyToClipboard(code.code, code.id)}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              {copiedCode === code.id ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-400" />
                              )}
                            </button>
                          </div>
                          <p className="text-sm text-gray-500">
                            {code.discountType === 'percentage' 
                              ? `${code.discountValue}% off`
                              : `$${code.discountValue} off`
                            }
                            {code.maxUses && ` • ${code.usedCount || 0}/${code.maxUses} uses`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {code.isAffiliate && (
                          <Badge variant="outline" className="text-purple-600 border-purple-200">
                            <Users className="w-3 h-3 mr-1" />
                            {code.affiliateName}
                          </Badge>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => editPromo(code)}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePromo(code.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </DataCard>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Partner Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Partner</DialogTitle>
            <DialogDescription>Add a new partner for revenue sharing</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name <span className="text-red-500">*</span></Label>
              <Input
                value={newPartner.name}
                onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })}
                placeholder="Partner name"
              />
            </div>
            <div>
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={newPartner.email}
                onChange={(e) => setNewPartner({ ...newPartner, email: e.target.value })}
                placeholder="partner@example.com"
              />
            </div>
            <div>
              <Label>Partner Type</Label>
              <Select value={newPartner.partnerType} onValueChange={(v) => setNewPartner({ ...newPartner, partnerType: v })}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="service_partner">Service Partner</SelectItem>
                  <SelectItem value="affiliate">Affiliate</SelectItem>
                  <SelectItem value="referrer">Referrer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Revenue Split %</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={newPartner.defaultSplitPercentage}
                onChange={(e) => setNewPartner({ ...newPartner, defaultSplitPercentage: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="h-10">Cancel</Button>
            <Button onClick={handleAddPartner} disabled={saving} className="h-10 bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Add Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Service Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Link Partner Service</DialogTitle>
            <DialogDescription>Enter the share token from your partner</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Share Token <span className="text-red-500">*</span></Label>
              <Input
                value={shareToken}
                onChange={(e) => setShareToken(e.target.value)}
                placeholder="Paste token here"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLinkDialog(false)} className="h-10">Cancel</Button>
            <Button onClick={handleLinkService} disabled={linking} className="h-10 bg-blue-600 hover:bg-blue-700 text-white">
              {linking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Link Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promo Code Dialog */}
      <Dialog open={showPromoModal} onOpenChange={(open) => { if (!open) { setShowPromoModal(false); resetPromoForm(); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCode ? 'Edit' : 'Create'} Promo Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Code <span className="text-red-500">*</span></Label>
              <Input
                value={promoFormData.code}
                onChange={(e) => setPromoFormData({ ...promoFormData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., SAVE20"
                maxLength={20}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discount Type</Label>
                <Select value={promoFormData.discountType} onValueChange={(v) => setPromoFormData({ ...promoFormData, discountType: v })}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  value={promoFormData.discountValue}
                  onChange={(e) => setPromoFormData({ ...promoFormData, discountValue: e.target.value })}
                  placeholder={promoFormData.discountType === 'percentage' ? '20' : '10'}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Max Uses (optional)</Label>
                <Input
                  type="number"
                  value={promoFormData.maxUses}
                  onChange={(e) => setPromoFormData({ ...promoFormData, maxUses: e.target.value })}
                  placeholder="Unlimited"
                />
              </div>
              <div>
                <Label>Expires in Days</Label>
                <Input
                  type="number"
                  value={promoFormData.expiresInDays}
                  onChange={(e) => setPromoFormData({ ...promoFormData, expiresInDays: e.target.value })}
                  placeholder="Never"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={promoFormData.isAffiliate}
                onCheckedChange={(checked) => setPromoFormData({ ...promoFormData, isAffiliate: checked })}
              />
              <Label className="cursor-pointer">This is an affiliate code</Label>
            </div>
            {promoFormData.isAffiliate && (
              <div className="grid grid-cols-2 gap-3 pl-6">
                <div>
                  <Label>Affiliate Name</Label>
                  <Input
                    value={promoFormData.affiliateName}
                    onChange={(e) => setPromoFormData({ ...promoFormData, affiliateName: e.target.value })}
                    placeholder="Name"
                  />
                </div>
                <div>
                  <Label>Commission %</Label>
                  <Input
                    type="number"
                    value={promoFormData.affiliateCommission}
                    onChange={(e) => setPromoFormData({ ...promoFormData, affiliateCommission: e.target.value })}
                    placeholder="10"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowPromoModal(false); resetPromoForm(); }} className="h-10">Cancel</Button>
            <Button onClick={handlePromoSubmit} className="h-10 bg-blue-600 hover:bg-blue-700 text-white">
              {editingCode ? 'Update' : 'Create'} Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};

export default PartnersAndPromosTab;
